<script setup lang="ts">
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useCreateOutboundDeliveryApi, type CreateOutboundDelivery } from './api/createOutboundDeliveryApi';
import { useUpdateOutboundDeliveryApi, type UpdateOutboundDelivery } from './api/updateOutboundDeliveryApi';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { storeToRefs } from 'pinia';
import { useOutboundDeliveryStore } from './stores/outboundDeliveryStore';
import { useRefreshOutboundDeliveryStore } from './stores/refreshOutboundDeliveryStore';
import { useRoute, useRouter } from 'vue-router';
import type { OutboundDelivery } from './domain/outboundDelivery';
import { computed, onMounted, ref } from 'vue';
import { cloneObject } from 'shared/common/domain/cloneObject';
import MainLayout from '@/common/components/MainLayout.vue';
import { Button, Card, Column, DataTable, InputNumber, InputText, Message, Select, Textarea, DatePicker, ProgressBar } from 'primevue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { storageAreaMatchesProductType } from './domain/storageAreaMatchesProductType';
import { convertToDisplayUnit, convertToTon, Unit } from 'shared/common/domain/bulkUnits';
import { DeliveryStatus } from 'shared/deliveries/domain/deliveryStatus';

interface EditableOutboundDeliveryRow {
  rowId: string;
  id: string;
  productId: string | null;
  storageAreaId: string | null;
  weight: number | null;
  volume: number | null;
  note: string | null;
}

interface SelectOption {
  label: string;
  value: string;
}

const createApi = useCreateOutboundDeliveryApi();
const updateApi = useUpdateOutboundDeliveryApi();
const actionFailedAlert = useActionFailedAlert();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const productStore = useProductStore();
const storageAreaStore = useStorageAreaStore();
const store = useOutboundDeliveryStore();
const refreshStore = useRefreshOutboundDeliveryStore();

const route = useRoute();
const router = useRouter();

const current = ref<OutboundDelivery | null>(null);
const loading = ref(false);
const initialLoading = ref(Boolean(route.params.id));

const rows = ref<EditableOutboundDeliveryRow[]>([]);

const productOptions = computed<SelectOption[]>(() => {
  return productStore.sortedByName.map((item) => ({
    label: item.name,
    value: item.id,
  }));
});

const storageAreaOptionsByProductId = computed<Map<string, SelectOption[]>>(() => {
  const map = new Map<string, SelectOption[]>();

  for (const product of productStore.sortedByName) {
    const options = storageAreaStore.storageAreas
      .filter((area) => storageAreaMatchesProductType(product.type, area.type))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((area) => ({
        label: area.name,
        value: area.id,
      }));

    map.set(product.id, options);
  }

  return map;
});

const name = ref<string>('');
const nameChanged = computed(() => {
  if (!current.value) return false;
  return current.value.name !== name.value;
});

const customer = ref<string | null>(null);
const customerChanged = computed(() => {
  if (!current.value) return false;
  return current.value.customer !== customer.value;
});

const customerReference = ref<string | null>(null);
const customerReferenceChanged = computed(() => {
  if (!current.value) return false;
  return current.value.customerReference !== customerReference.value;
});

const note = ref<string | null>(null);
const noteChanged = computed(() => {
  if (!current.value) return false;
  return current.value.note !== note.value;
});

const plannedStart = ref<Date | null>(null);
const plannedStartChanged = computed(() => {
  if (!current.value) return false;
  const currentPlannedStart = current.value.plannedStartUtc ? new Date(current.value.plannedStartUtc) : null;
  return currentPlannedStart?.getTime() !== plannedStart.value?.getTime();
});

const plannedFinish = ref<Date | null>(null);
const plannedFinishChanged = computed(() => {
  if (!current.value) return false;
  const currentPlannedFinish = current.value.plannedFinishUtc ? new Date(current.value.plannedFinishUtc) : null;
  return currentPlannedFinish?.getTime() !== plannedFinish.value?.getTime();
});

interface StatusOption {
  name: string;
  value: DeliveryStatus;
}
const statusList = [
  DeliveryStatus.Planned,
  DeliveryStatus.Released,
  DeliveryStatus.Active,
  DeliveryStatus.Finished,
  DeliveryStatus.Invoiced,
];
const statusOptions = statusList.map<StatusOption>((t) => {
  return {
    value: t,
    name: translations.value.enums.deliveryStatus(t),
  };
});
const status = ref<DeliveryStatus>(DeliveryStatus.Planned);
const statusChanged = computed(() => {
  if (!current.value) return false;
  return current.value.status !== status.value;
});

const rowsChanged = computed(() => {
  if (!current.value) {
    return rows.value.length > 0;
  }

  const currentRowsSignature = JSON.stringify(
    current.value.rows.map((row) => ({
      id: row.id,
      productId: row.productId,
      storageAreaId: row.storageAreaId,
      weight: row.weight,
      volume: row.volume,
      note: row.note,
    })),
  );

  const editedRowsSignature = JSON.stringify(
    rows.value.map((row) => ({
      id: row.id,
      productId: row.productId,
      storageAreaId: row.storageAreaId,
      weight: tonFromDisplayWeight(row.weight, row.productId),
      volume: row.volume,
      note: row.note,
    })),
  );

  return currentRowsSignature !== editedRowsSignature;
});

const rowValidationErrors = computed(() => {
  if (rows.value.every((row) => row.productId)) {
    return [] as string[];
  }

  return [translations.value.outboundDelivery.editPage.productRequired];
});

const invalid = computed(() => rowValidationErrors.value.length > 0);
const changed = computed(() => nameChanged.value || rowsChanged.value || customerChanged.value || customerReferenceChanged.value || noteChanged.value || plannedStartChanged.value || plannedFinishChanged.value || statusChanged.value);
const saveEnabled = computed(() => (current.value === null || changed.value) && !invalid.value);

function storageAreaOptionsForProduct(productId: string | null) {
  if (!productId) {
    return [] as SelectOption[];
  }

  return storageAreaOptionsByProductId.value.get(productId) ?? [];
}

function createEmptyRow(): EditableOutboundDeliveryRow {
  return {
    rowId: crypto.randomUUID(),
    id: '',
    productId: null,
    storageAreaId: null,
    weight: null,
    volume: null,
    note: null,
  };
}

function displayWeightFromTon(weightTon: number | null, productId: string | null): number | null {
  // FIXME: move to reusable methods
  if (weightTon === null || !productId) {
    return weightTon;
  }

  const bulkData = productStore.getById(productId)?.bulkData;
  if (!bulkData) {
    return weightTon;
  }

  if (bulkData.unit === Unit.CubicMeter && bulkData.density <= 0) {
    return weightTon;
  }

  return convertToDisplayUnit(weightTon, bulkData.unit, bulkData.density);
}

function tonFromDisplayWeight(weight: number | null, productId: string | null): number | null {
  // FIXME: move to reusable methods
  if (weight === null || !productId) {
    return weight;
  }

  const bulkData = productStore.getById(productId)?.bulkData;
  if (!bulkData) {
    return weight;
  }

  if (bulkData.unit === Unit.CubicMeter && bulkData.density <= 0) {
    return weight;
  }

  return convertToTon(weight, bulkData.unit, bulkData.density);
}

function productUnit(productId: string | null): Unit {
  if (!productId) {
    return Unit.MetricTon;
  }

  return productStore.getById(productId)?.bulkData?.unit ?? Unit.MetricTon;
}

function mapFromDelivery(delivery: OutboundDelivery) {
  rows.value = delivery.rows.map((row) => ({
    rowId: row.id,
    id: row.id,
    productId: row.productId,
    storageAreaId: row.storageAreaId,
    weight: displayWeightFromTon(row.weight, row.productId),
    volume: row.volume,
    note: row.note,
  }));
}

function addRow() {
  rows.value.push(createEmptyRow());
}

function removeRow(rowId: string) {
  rows.value = rows.value.filter((row) => row.rowId !== rowId);
}

function updateRowProduct(row: EditableOutboundDeliveryRow, productId: string | null) {
  const previousProductId = row.productId;
  const weightTon = tonFromDisplayWeight(row.weight, previousProductId);

  row.productId = productId;
  row.weight = displayWeightFromTon(weightTon, productId);

  const options = storageAreaOptionsForProduct(productId);
  const isStorageAllowed = options.some((option) => option.value === row.storageAreaId);
  if (!isStorageAllowed) {
    row.storageAreaId = null;
  }
}

function rowForPayload(row: EditableOutboundDeliveryRow) {
  if (!row.productId) {
    throw new Error('Outbound delivery row requires productId.');
  }

  return {
    ...(row.id ? { id: row.id } : {}),
    productId: row.productId,
    storageAreaId: row.storageAreaId,
    weight: tonFromDisplayWeight(row.weight, row.productId),
    volume: row.volume,
    note: row.note
  };
}

function currentDeliveryRow(rowId: string) {
  if (!rowId || !current.value) {
    return null;
  }

  return current.value.rows.find((row) => row.id === rowId) ?? null;
}

function deliveredWeightForRow(row: EditableOutboundDeliveryRow): number | null {
  const currentRow = currentDeliveryRow(row.id);
  return displayWeightFromTon(currentRow?.deliveredWeight ?? null, row.productId);
}

function deliveredVolumeForRow(row: EditableOutboundDeliveryRow): number | null {
  const currentRow = currentDeliveryRow(row.id);
  return currentRow?.deliveredVolume ?? null;
}

function disableRow(row: EditableOutboundDeliveryRow): boolean {
  const currentRow = currentDeliveryRow(row.id);

  const deliveredVolume = currentRow?.deliveredVolume ?? 0;
  const deliveredWeight = currentRow?.deliveredWeight ?? 0

  return (deliveredVolume > 0 || deliveredWeight > 0);
}

const datePickerLocale = computed(() => {
  return languageStore.getPrimeVueLocale();
});

const dateFormat = computed(() => {
  return languageStore.getPrimeVueDateFormat();
});

onMounted(async () => {
  const id = route.params.id as string | undefined;
  if (!id) return;
  try {
    await refreshStore.refresh();
    const delivery = store.getById(id);
    if (!delivery) return;

    name.value = delivery.name;
    status.value = delivery.status;
    customer.value = delivery.customer;
    customerReference.value = delivery.customerReference;
    note.value = delivery.note;
    plannedStart.value = delivery.plannedStartUtc ? new Date(delivery.plannedStartUtc) : null;
    plannedFinish.value = delivery.plannedFinishUtc ? new Date(delivery.plannedFinishUtc) : null;

    current.value = cloneObject(delivery);
    mapFromDelivery(delivery);
  } finally {
    initialLoading.value = false;
  }
});

function cancel() {
  router.push({ name: 'delivery', query: { tab: 'outbound-deliveries' } });
}

async function save() {
  if (invalid.value) {
    return;
  }

  try {
    loading.value = true;
    const deliveryId = current.value?.id;
    if (deliveryId) {
      const delivery: UpdateOutboundDelivery = {
        id: deliveryId,
        name: name.value ?? '',
        customer: customer.value,
        customerReference: customerReference.value,
        note: note.value,
        status: status.value,
        plannedStartUtc: plannedStart.value ? plannedStart.value.toISOString() : null,
        plannedFinishUtc: plannedFinish.value ? plannedFinish.value.toISOString() : null,
        rows: rows.value.map(rowForPayload),
      };

      const response = await updateApi.execute({ id: deliveryId, entity: delivery });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      name.value = response.delivery.name;
      current.value = response.delivery;
      mapFromDelivery(response.delivery);
    } else {
      console.log(rows);

      const delivery: CreateOutboundDelivery = {
        name: name.value ?? '',
        customer: customer.value,
        customerReference: customerReference.value,
        note: note.value,
        status: status.value,
        plannedStartUtc: plannedStart.value ? plannedStart.value.toISOString() : null,
        plannedFinishUtc: plannedFinish.value ? plannedFinish.value.toISOString() : null,
        rows: rows.value.map(rowForPayload),
      };

      const response = await createApi.execute({ entity: delivery });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      current.value = response.delivery;

      name.value = response.delivery.name;
      customer.value = response.delivery.customer;
      customerReference.value = response.delivery.customerReference;
      note.value = response.delivery.note;

      mapFromDelivery(response.delivery);
    }
    await refreshStore.refresh();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <MainLayout>
    <Card class="outbound-delivery-card">
      <template #title>
        <span>{{
          current || initialLoading
            ? translations.outboundDelivery.editPage.editTitle
            : translations.outboundDelivery.editPage.createTitle
        }}</span>
      </template>
      <template #content>
        <div v-if="initialLoading" role="status" class="flex flex-col gap-2 p-4">
          <span>{{ translations.generic.loadingTableData }}</span>
          <ProgressBar mode="indeterminate" :showValue="false" />
        </div>
        <div v-else class="outbound-delivery-grid">
          <Message v-for="error in rowValidationErrors" :key="error" severity="warn" size="small" variant="simple">
            {{ error }}
          </Message>

          <div class="outbound-delivery-form">
            <div class="flex gap-2">
              <div class="field" :class="{ changed: nameChanged }">
                <label for="edit-outbound-delivery-name">{{ translations.generic.name }}</label>
                <InputText autocomplete="off" autofocus id="edit-outbound-delivery-name" v-model:model-value="name">
                </InputText>
              </div>

              <div class="field" :class="{ changed: statusChanged }">
                <label for="edit-outbound-delivery-status">{{ translations.generic.status }}</label>
                <Select id="edit-outbound-delivery-status" name="state" :options="statusOptions" option-label="name"
                  option-value="value" v-model="status">
                </Select>
              </div>
            </div>

            <div class="flex gap-2">
              <div class="field" :class="{ changed: customerChanged }">
                <label for="edit-outbound-delivery-customer">{{ translations.generic.customer }}</label>
                <InputText autocomplete="off" autofocus id="edit-outbound-delivery-customer"
                  v-model:model-value="customer">
                </InputText>
              </div>

              <div class="field" :class="{ changed: customerReferenceChanged }">
                <label for="edit-outbound-delivery-customerReference">{{ translations.generic.customerReference
                  }}</label>
                <InputText autocomplete="off" autofocus id="edit-outbound-delivery-customerReference"
                  v-model:model-value="customerReference">
                </InputText>
              </div>
            </div>

            <div class="flex gap-2">
              <div class="field" :class="{ changed: plannedStartChanged }">
                <label for="edit-outbound-delivery-plannedStart">{{ translations.delivery.plannedStart }}</label>
                <DatePicker autocomplete="off" autofocus id="edit-outbound-delivery-plannedStart"
                  v-model:model-value="plannedStart" :locale="datePickerLocale" :dateFormat="dateFormat">
                </DatePicker>
              </div>

              <div class="field" :class="{ changed: plannedFinishChanged }">
                <label for="edit-outbound-delivery-plannedFinish">{{ translations.delivery.plannedFinish }}</label>
                <DatePicker autocomplete="off" autofocus id="edit-outbound-delivery-plannedFinish"
                  v-model:model-value="plannedFinish" :locale="datePickerLocale" :dateFormat="dateFormat">
                </DatePicker>
              </div>
            </div>

            <div class="field" :class="{ changed: noteChanged }">
              <label for="edit-outbound-delivery-note">{{ translations.generic.note }}</label>
              <Textarea autocomplete="off" autofocus id="edit-outbound-delivery-note" v-model:model-value="note">
              </Textarea>
            </div>
          </div>

          <div class="component-area">
            <div class="rows-toolbar">
              <h3>{{ translations.outboundDelivery.editPage.rowsTitle }}</h3>
              <Button icon="pi pi-plus" :label="translations.outboundDelivery.editPage.addRow" @click="addRow"></Button>
            </div>

            <DataTable :value="rows" class="rows-table" size="small">
              <template #empty>{{ translations.outboundDelivery.editPage.noRows }}</template>

              <Column field="productId" :header="translations.generic.product">
                <template #body="{ data }">
                  <Select :modelValue="data.productId" :options="productOptions" optionLabel="label" optionValue="value"
                    filter showClear :placeholder="translations.generic.product"
                    @update:modelValue="updateRowProduct(data, $event ?? null)" :disabled="disableRow(data)"></Select>
                </template>
              </Column>

              <Column field="storageAreaId" :header="translations.generic.storageArea">
                <template #body="{ data }">
                  <Select v-model="data.storageAreaId" :options="storageAreaOptionsForProduct(data.productId)"
                    optionLabel="label" optionValue="value" filter showClear
                    :placeholder="translations.generic.storageArea"
                    :disabled="!data.productId || disableRow(data)"></Select>
                </template>
              </Column>

              <Column field="weight" :header="translations.outboundDelivery.editPage.rowWeight">
                <template #body="{ data }">
                  <InputGroup>
                    <InputNumber v-model:model-value="data.weight" :min="0" :maxFractionDigits="3"
                      :disabled="disableRow(data)" :input-id="`outbound-delivery-row-weight-${data.rowId}`">
                    </InputNumber>
                    <InputGroupAddon>{{ translations.bulk.bulkUnitShort(productUnit(data.productId)) }}
                    </InputGroupAddon>
                  </InputGroup>
                </template>
              </Column>

              <Column field="volume" :header="translations.outboundDelivery.editPage.rowVolume">
                <template #body="{ data }">
                  <InputNumber v-model="data.volume" :min="0" :maxFractionDigits="3" suffix=" m3"
                    :disabled="disableRow(data)"></InputNumber>
                </template>
              </Column>

              <Column field="note" :header="translations.generic.note">
                <template #body="{ data }">
                  <InputText v-model="data.note" :disabled="disableRow(data)"></InputText>
                </template>
              </Column>

              <Column v-if="current" field="deliveredWeight"
                :header="translations.outboundDelivery.editPage.rowDeliveredWeight">
                <template #body="{ data }">
                  <span class="text-muted font-mono">
                    {{ deliveredWeightForRow(data) ?? '0' }} {{
                      translations.bulk.bulkUnitShort(productUnit(data.productId)) }}
                  </span>
                </template>
              </Column>

              <Column v-if="current" field="deliveredVolume"
                :header="translations.outboundDelivery.editPage.rowDeliveredVolume">
                <template #body="{ data }">
                  <span class="text-muted font-mono">
                    {{ deliveredVolumeForRow(data) ?? '0' }} m3
                  </span>
                </template>
              </Column>

              <Column header="">
                <template #body="{ data }">
                  <Button text severity="danger" icon="pi pi-trash" :disabled="disableRow(data)"
                    @click="removeRow(data.rowId)"></Button>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="!initialLoading" class="actions">
          <Button class="save-button" :loading="loading" :disabled="!saveEnabled" @click="save" icon="pi pi-save"
            :label="translations.generic.save">
          </Button>
          <Button type="button" class="ms-2" @click="cancel" :label="translations.generic.cancel"></Button>
        </div>
      </template>
    </Card>
  </MainLayout>
</template>

<style scoped>
:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto;
  column-gap: 0.5rem;
  align-items: center;
}

.save-button {
  grid-column: 3;
}

.outbound-delivery-card {
  height: 100%;
  overflow-y: hidden;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}

.outbound-delivery-grid {
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;
  height: 100%;
}

.outbound-delivery-form {
  display: grid;
  grid-template-columns: minmax(14rem, 30rem);
}

.rows-toolbar {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.rows-toolbar h3 {
  margin: 0;
}

.rows-table {
  width: 100%;
}

:deep(.delivered-field-group .p-inputgroupaddon) {
  background-color: var(--p-surface-100);
  color: var(--p-text-muted-color);
}

:deep(.delivered-field-input .p-inputtext) {
  background-color: var(--p-surface-100);
  color: var(--p-text-muted-color);
  opacity: 1;
}

.component-area {
  height: 100%;
  overflow-y: auto;
}
</style>
